/* ==========================================================================
   Crumpaw · Wraparound Steel Bowl — personalisation controller.

   Drives <cpwb-personalizer> only. cp-signature-bowl.js is loaded alongside
   this file and is NOT modified: it still supplies <cpsb-lightbox>,
   <cpsb-sticky>, <cpsb-toast>, <cpsb-scroll-cta> and <cpsb-countdown>. Because
   this section never emits a <cpsb-personalizer> tag, that controller's
   personaliser class is never instantiated here and the two cannot collide.

   The difference from the Signature Bowl is the artwork: instead of one
   centred name with motifs beside it, the name and its glyphs repeat as a
   strip that is projected onto the cylinder in two or more staggered rows.

   Shared DOM hooks keep their data-cpsb names so the shared stylesheet and the
   shared sticky bar keep working untouched. Only the controls this page adds
   — pattern, rows, the ten-colour ink grid and the print-colour advisory —
   use data-cpwb.

   Degradation contract, unchanged from the Signature Bowl: if anything here
   throws, the buy form still works. The personalisation radios carry their own
   `properties[...]` values, and Add to cart is never left disabled by a
   preview error.
   ========================================================================== */

(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;

  /* ---------------------------------------------------------------- helpers */

  const clamp = (min, value, max) => Math.max(min, Math.min(max, value));

  const readConfig = (root) => {
    const node = root.querySelector('[data-cpwb-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[cpwb] could not parse config', error);
      return null;
    }
  };

  /** Minimal Shopify money formatter — same contract as the Signature Bowl. */
  /**
   * What a tier takes off, in cents.
   *
   * The twin of tierOff() in cp-signature-bowl.js. This section has its own
   * controller but reads the same `discountPct` section setting, so the two have
   * to agree - and both have to agree with Shopify, which applies a percentage
   * per unit and floors it. 10% of a $49.99 bowl is 499c, not 500c.
   */
  const tierOff = (unit, tier) => {
    const pct = Number(tier && tier.discountPct) || 0;
    if (pct <= 0) return 0;
    return Math.floor((unit * pct) / 100) * (Number(tier.quantity) || 1);
  };

  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;

    const withDelimiters = (value, precision, thousands, decimal) => {
      const fixed = (value / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    if (typeof format !== 'string' || !format.includes('{{')) {
      return `$${withDelimiters(amount, 2, ',', '.')}`;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      switch (token) {
        case 'amount':
          return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(amount, 2, "'", '.');
        default:
          return withDelimiters(amount, 2, ',', '.');
      }
    });
  };

  const interpolate = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    );

  /* ------------------------------------------------------ colour distance */

  const toRgb = (hex) => {
    const value = String(hex || '').trim().replace('#', '');
    const full =
      value.length === 3
        ? value
            .split('')
            .map((c) => c + c)
            .join('')
        : value.padEnd(6, '0').slice(0, 6);
    const int = parseInt(full, 16);
    if (Number.isNaN(int)) return [0, 0, 0];
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  };

  /** Straight RGB euclidean distance — the original design's measure. */
  const rgbDistance = (a, b) => {
    const x = toRgb(a);
    const y = toRgb(b);
    return Math.sqrt((x[0] - y[0]) ** 2 + (x[1] - y[1]) ** 2 + (x[2] - y[2]) ** 2);
  };

  /**
   * WCAG relative luminance contrast ratio. Lightness, not hue, decides
   * whether print reads on a coloured band, so this is the default measure —
   * RGB distance scores pale yellow on bare steel as "far apart" when in
   * practice it is invisible.
   */
  const relativeLuminance = (hex) => {
    const [r, g, b] = toRgb(hex).map((channel) => {
      const c = channel / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const contrastRatio = (a, b) => {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  };

  /* ----------------------------------------------------- glyph proportions */

  /**
   * Per-glyph optical sizing, ported from the design. `scale` tunes the drawn
   * size so all four read at the same weight; `width` is how much horizontal
   * room the glyph claims in the repeating strip, as a multiple of the
   * lettering size.
   */
  const GLYPHS = {
    paw: { scale: 0.9, width: 1.6 },
    heart: { scale: 0.86, width: 1.6 },
    bone: { scale: 1.05, width: 2.1 },
    crown: { scale: 0.9, width: 1.6 },
  };

  /**
   * How far a word's ink sits below the middle of its own line box, in em.
   *
   * The band centres each item by its box. A text box is the font's ascent and
   * descent; the ink is the shape actually drawn inside it, and for a name in
   * mostly lowercase the two do not share a centre. Canvas reports both, so the
   * gap is measured per typeface and per word instead of being guessed at or
   * patched font by font.
   *
   * Returned in em, so it rides the cqw lettering size and stays right at every
   * width. Zero whenever the browser will not report the metrics, which leaves
   * the previous behaviour rather than a broken one.
   */
  const INK_OFFSETS = new Map();
  let inkCanvas = null;

  const inkOffset = (family, weight, text) => {
    if (!text) return 0;
    const key = `${weight}|${family}|${text}`;
    if (INK_OFFSETS.has(key)) return INK_OFFSETS.get(key);

    let offset = 0;
    try {
      inkCanvas = inkCanvas || document.createElement('canvas').getContext('2d');
      inkCanvas.font = `${weight} 100px ${family}`;
      const m = inkCanvas.measureText(text);
      const fa = m.fontBoundingBoxAscent;
      const fd = m.fontBoundingBoxDescent;
      const aa = m.actualBoundingBoxAscent;
      const ad = m.actualBoundingBoxDescent;
      const ok = [fa, fd, aa, ad].every(
        (v) => typeof v === 'number' && Number.isFinite(v)
      );
      if (ok) {
        // line-height is 1, so the line box is the 100px em and the baseline
        // sits half the leading below its top.
        const baseline = (100 - (fa + fd)) / 2 + fa;
        const centre = baseline - (aa - ad) / 2;
        // A correction this large would mean the metrics are nonsense; leave
        // the item where it was rather than throw it off the band.
        offset = clamp(-0.2, (50 - centre) / 100, 0.2);
      }
    } catch (err) {
      offset = 0;
    }

    INK_OFFSETS.set(key, offset);
    return offset;
  };

  /* ------------------------------------------------------- state broadcast */

  // Deliberately the Signature Bowl's event name: the shared sticky bar in
  // cp-sb-sticky-bar.liquid listens for exactly this.
  const STATE_EVENT = 'cpsb:state';

  /* =========================================================================
     <cpwb-personalizer>
     ========================================================================= */

  class CpwbPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cpwbReady === 'true') return;

      this.config = readConfig(this);
      if (!this.config) return;

      this.dataset.cpwbReady = 'true';

      const c = this.config;
      const last = (list) => Math.max(0, (list || []).length - 1);

      this.state = {
        colour: c.initialColour || 0,
        size: c.initialSize || 0,
        font: clamp(0, c.initialFont || 0, last(c.fonts)),
        pattern: clamp(0, c.initialPattern || 0, last(c.patterns)),
        density: clamp(0, c.initialDensity || 0, last(c.densities)),
        tilt: 0,
        ink: null, // null = inherit the colourway default
        tier: c.initialTier || 0,
        name: '',
      };

      this.#collectRefs();
      this.#syncInitialRadios();
      this.#bind();
      this.#render();

      // The first render can measure a fallback face if the webfonts are still
      // loading, which would leave the lettering centred to the wrong metrics.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready
          .then(() => {
            INK_OFFSETS.clear();
            this.#render();
          })
          .catch(() => {});
      }
    }

    disconnectedCallback() {
      this.dataset.cpwbReady = 'false';
      this.#listeners.forEach(([target, type, handler, capture]) =>
        target.removeEventListener(type, handler, capture)
      );
      this.#listeners = [];
    }

    #listeners = [];

    /** Tracks the last computed validity so the submit guard stays cheap. */
    #lastValid = false;

    /** Whether the opening photograph has already been dismissed. */
    #uncovered = false;

    /**
     * Hand the stage over from the product's opening photograph to the live
     * preview. This section has its own controller rather than
     * <cpsb-personalizer>, so it needs its own copy of this - the markup and
     * the CSS are the shared ones.
     */
    #uncover() {
      if (this.#uncovered) return;
      this.#uncovered = true;
      document.querySelectorAll('.cpsb-preview__cover').forEach((cover) => {
        cover.hidden = true;
      });
    }

    #on(target, type, handler, capture = false) {
      if (!target) return;
      target.addEventListener(type, handler, capture);
      this.#listeners.push([target, type, handler, capture]);
    }

    #collectRefs() {
      const q = (name) => this.querySelector(`[data-cpsb="${name}"]`);
      const qa = (name) => Array.from(this.querySelectorAll(`[data-cpsb="${name}"]`));
      const qw = (name) => this.querySelector(`[data-cpwb="${name}"]`);
      const qwa = (name) => Array.from(this.querySelectorAll(`[data-cpwb="${name}"]`));

      this.refs = {
        // Indexed by colour block position so a colour block with no image
        // cannot shift the array out of step with the radios.
        bases: (() => {
          const map = [];
          this.querySelectorAll('[data-cpsb-colour-index]').forEach((img) => {
            map[Number(img.dataset.cpsbColourIndex)] = img;
          });
          return map;
        })(),
        frame: q('preview-frame'),
        configLabel: q('preview-config'),

        // The colour row, which becomes a horizontal rail on mobile.
        chips: this.querySelector('.cpsb-chips'),

        // the wrap
        wrapLayer: qw('wrap-layer'),
        wrapSr: qw('wrap-sr'),

        // inputs
        variantId: this.querySelector('[data-cpsb="variant-id"]'),
        quantity: q('quantity'),
        nameInput: q('name-input'),
        counter: q('name-counter'),
        hint: q('name-hint'),
        error: q('name-error'),

        // radios — shared with the Signature Bowl
        colourInputs: qa('colour-input'),
        sizeInputs: qa('size-input'),
        fontInputs: qa('font-input'),
        inkInputs: qa('ink-input'),
        tierInputs: qa('tier-input'),
        swatches: qa('swatch'),

        // radios — wraparound only
        patternInputs: qwa('pattern-input'),
        densityInputs: qwa('density-input'),
        tiltInputs: qwa('tilt-input'),
        tiltLabel: qw('tilt-label'),

        // labels / readouts
        colourLabels: qa('colour-label'),
        sizeLabels: qa('size-label'),
        inkLabel: q('ink-label'),
        fontLabel: q('font-label'),
        patternLabel: qw('pattern-label'),
        densityLabel: qw('density-label'),

        contrast: qw('contrast'),
        contrastText: qw('contrast-text'),

        stockLeft: q('stock-left'),
        stockPct: q('stock-pct'),
        stockFill: q('stock-fill'),

        priceNow: q('price-now'),
        priceWas: q('price-was'),
        priceSave: q('price-save'),
        installment: q('installment'),

        // The Add to cart button comes from the theme's own snippet, so it is
        // found by the class we handed that snippet rather than a data hook.
        cta: this.querySelector('button.cpsb-cta'),
        ctaLabel:
          this.querySelector('button.cpsb-cta .add-to-cart-text__content span span') ||
          this.querySelector('button.cpsb-cta .add-to-cart-text__content'),
        form: this.querySelector('form[data-type="add-to-cart-form"]'),

        sizePrices: qa('size-price'),
        tierPrices: qa('tier-price'),
        tierSaves: qa('tier-save'),
        tierTags: qa('tier-tag'),

        fontSamples: qa('font-sample'),
      };
    }

    /**
     * Liquid marks the first option in each group `checked`. Where the merchant
     * has chosen a different default, move the mark before the first paint so
     * the form and the artwork start out agreeing.
     */
    #syncInitialRadios() {
      const { refs, state } = this;
      const set = (list, index) => {
        const target = list[index];
        if (target && !target.disabled) target.checked = true;
      };
      set(refs.fontInputs, state.font);
      set(refs.patternInputs, state.pattern);
      set(refs.densityInputs, state.density);
      set(refs.tiltInputs, state.tilt);
    }

    #bind() {
      const { refs } = this;

      /* The opening photograph steps aside as soon as the shopper starts
         configuring. Both events bubble, so one pair of listeners on the host
         covers every control; the swatch buttons set `.checked` in code and
         fire neither, so they call it themselves below. */
      this.#on(this, 'change', () => this.#uncover());
      this.#on(this, 'input', () => this.#uncover());

      refs.colourInputs.forEach((input, index) =>
        this.#on(input, 'change', () => {
          // Matches the design: changing colourway drops the ink override.
          this.state.colour = index;
          this.state.ink = null;
          this.#render();
          this.#revealColour();
        })
      );

      refs.swatches.forEach((button, index) =>
        this.#on(button, 'click', (event) => {
          event.preventDefault();
          this.#uncover();
          this.state.colour = index;
          this.state.ink = null;
          const target = refs.colourInputs[index];
          if (target) target.checked = true;
          this.#render();
          // The swatch strip is a separate control, so the rail will not have
          // followed the selection on its own.
          this.#revealColour();
        })
      );

      const simple = (list, key) =>
        list.forEach((input, index) =>
          this.#on(input, 'change', () => {
            this.state[key] = index;
            this.#render();
          })
        );

      simple(refs.sizeInputs, 'size');
      simple(refs.fontInputs, 'font');
      simple(refs.patternInputs, 'pattern');
      simple(refs.densityInputs, 'density');
      simple(refs.tiltInputs, 'tilt');
      simple(refs.inkInputs, 'ink');
      simple(refs.tierInputs, 'tier');

      if (refs.nameInput) {
        this.#on(refs.nameInput, 'input', () => {
          this.state.name = this.#sanitize(refs.nameInput.value);
          if (refs.nameInput.value !== this.state.name) {
            refs.nameInput.value = this.state.name;
          }
          this.#render();
        });
      }

      // Belt and braces on validation. The disabled attribute is the visible
      // affordance, but the theme's own form code also manages that button, so
      // block the submit in the capture phase rather than trusting `disabled`.
      const guard = (event) => {
        if (this.#lastValid) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const input = refs.nameInput;
        if (input) {
          /* Focus without moving anything, then scroll once. Letting the focus
             do its own scrolling jumps the page, and the smooth scroll then
             runs from wherever it landed. */
          input.focus({ preventScroll: true });
          input.scrollIntoView({ block: 'center', behavior: 'smooth' });
          this.#nudge(input);
        }
      };
      this.#on(refs.cta, 'click', guard, true);
      this.#on(refs.form, 'submit', guard, true);

      // Theme editor: reveal and select the block the merchant clicked.
      this.#on(document, 'shopify:block:select', (event) => {
        const id = event.detail && event.detail.blockId;
        if (!id) return;
        const target = this.querySelector(
          `[data-cpwb-block-id="${id}"], [data-cpsb-block-id="${id}"]`
        );
        if (!target) return;
        const input = target.matches('input') ? target : target.querySelector('input');
        if (input && !input.disabled) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }

    /* ------------------------------------------------------ mobile rail */

    /**
     * Centre the selected chip on the mobile colour rail.
     *
     * Only the rail's own scrollLeft is moved. scrollIntoView would also
     * scroll the page vertically, which is wrong here: picking a colour from
     * the swatch strip should never drag the buy box up the screen.
     *
     * Called from the colour handlers rather than from #render, so the rail
     * never jumps on first paint.
     */
    #revealColour() {
      if (!(this.config.mobile && this.config.mobile.colourRail)) return;

      const rail = this.refs.chips;
      const input = this.refs.colourInputs[this.state.colour];
      if (!rail || !input) return;

      // Scrollable only on the mobile layout; on desktop the row still wraps.
      if (rail.scrollWidth <= rail.clientWidth + 1) return;

      const chip = input.nextElementSibling;
      if (!chip) return;

      const centred = chip.offsetLeft - (rail.clientWidth - chip.offsetWidth) / 2;
      rail.scrollLeft = Math.max(0, centred);
    }

    /* --------------------------------------------------------- name rules */

    #sanitize(value) {
      const { allowedPattern, maxLength } = this.config.validation;
      let out = String(value || '').normalize('NFC');
      if (allowedPattern) {
        try {
          /* Accented letters come first: the setting usually ends in a hyphen,
             which would otherwise start a range. */
          out = out.replace(new RegExp(`[^À-ÖØ-öø-ſ${allowedPattern}]`, 'g'), '');
        } catch {
          /* a bad pattern from the editor must not break typing */
        }
      }
      return out.slice(0, maxLength);
    }

    /* -------------------------------------------------------- computation */

    #derive() {
      const c = this.config;
      const s = this.state;

      const colour = c.colours[s.colour] || c.colours[0] || {};
      const size = c.sizes[s.size] || c.sizes[0] || {};
      const font = c.fonts[s.font] || c.fonts[0] || {};
      const pattern = c.patterns[s.pattern] || c.patterns[0] || { cycle: [] };
      const density = c.densities[s.density] || c.densities[0] || { rows: 2 };
      const tilt = (c.tilts || [])[s.tilt] || (c.tilts || [])[0] || { degrees: 0 };
      const inkIndex = s.ink == null ? colour.defaultInk ?? 0 : s.ink;
      const ink = c.inks[inkIndex] || c.inks[0] || {};
      const tier = c.tiers[s.tier] || c.tiers[0] || { quantity: 1, discount: 0 };

      const trimmed = s.name.trim();
      const shown = trimmed || c.copy.namePlaceholder;
      const text = font.caps ? shown.toUpperCase() : shown;

      const variant = this.#findVariant(colour.value, size.value);
      const unit = variant ? variant.price : 0;

      /* Kept in step with cp-signature-bowl.js, which this file is a fork of:
         the was-price is the compare-at when the variant carries one, so the
         row reports the ticket saving as well as the bundle discount rather
         than only the bundle. Patching one controller and not the other is how
         the wraparound bowl ends up the only bowl with no saving on it. */
      const full = variant && variant.compareAt > unit ? variant.compareAt : unit;
      const gross = full * tier.quantity;
      const net = Math.max(0, unit * tier.quantity - tierOff(unit, tier));
      const saved = Math.max(0, gross - net);

      return {
        tilt,
        colour,
        size,
        font,
        pattern,
        density,
        ink,
        inkIndex,
        tier,
        trimmed,
        text,
        variant,
        unit,
        gross,
        net,
        saved,
        savePct: gross > net ? Math.round((saved / gross) * 100) : 0,
        valid: trimmed.length >= (c.validation.minLength || 1),
      };
    }

    #findVariant(colourValue, sizeValue) {
      return (
        this.config.variants.find(
          (v) => v.option1 === colourValue && v.option2 === sizeValue
        ) || null
      );
    }

    /* ------------------------------------------------------------- render */

    #render() {
      let d;
      try {
        d = this.#derive();
      } catch (error) {
        console.warn('[cpwb] derive failed', error);
        this.#failSafe();
        return;
      }

      try {
        this.#renderWrap(d);
      } catch (error) {
        // A broken preview must never block the sale.
        console.warn('[cpwb] wrap render failed', error);
      }

      try {
        this.#renderForm(d);
        this.#renderPrices(d);
        this.#renderValidation(d);
        this.#renderFontSamples();
        this.#renderContrast(d);
        this.#broadcast(d);
      } catch (error) {
        console.warn('[cpwb] form render failed', error);
        this.#failSafe();
      }
    }

    /** Leave the form usable no matter what went wrong above. */
    #failSafe() {
      const cta = this.refs && this.refs.cta;
      if (cta && cta.dataset.cpsbSoldOut !== 'true') cta.disabled = false;
    }

    /* ----------------------------------------------------------- the wrap */

    /**
     * Lay the name and its glyphs around the coated band.
     *
     * The strip is built flat, then each item is mapped onto the visible face
     * of the cylinder: horizontal position follows sin(theta), the item is
     * squashed by cos(theta) as it turns away, dropped slightly to follow the
     * curve, and faded a little towards the edges. Items past the clip angle
     * are left out rather than drawn edge-on.
     */
    #renderWrap(d) {
      const { refs, config } = this;
      const layer = refs.wrapLayer;

      const w = config.wrap;
      const rows = Math.max(1, Number(d.density.rows) || 2);
      const deg = Number(d.tilt && d.tilt.degrees) || 0;
      const cycle = Array.isArray(d.pattern.cycle) ? d.pattern.cycle : [];

      if (layer) {
        // Arc geometry. `span` is the developed length of the visible arc, in
        // the same percentage units as the horizontal placement.
        const arc = (w.arcDegrees * Math.PI) / 180;
        const halfWidth = w.halfWidth;
        const span = (2 * halfWidth * arc) / Math.sin(arc);

        // More rows means a tighter stack, at the same ratio the design uses
        // between its two and three row layouts.
        const rowScale = rows <= 2 ? 1 : 0.82 ** (rows - 2);
        const length = Math.max(d.text.length, 3);

        // How many items a row carries is decided before anything is sized:
        // four across the two row layout, five across the three row layout,
        // as the approved design lays them out.
        //
        // This is the whole fix. The band used to size the lettering first and
        // then take however many items happened to fit, dropping the rest at
        // the clip angle with no floor — which is why a wide face like Allura
        // rendered two names to a row where Nunito rendered four, and why the
        // ends of the band sat empty. Fixing the count and shrinking the type
        // to meet it inverts that, and it does so from the font's own metrics
        // rather than any per-font exception.
        const perRow = Math.max(1, Math.min(rows <= 2 ? 4 : 5, w.maxRepeats));

        // Widths are carried as multiples of the type size, so a row can be
        // measured before the type size is known.
        const strips = [];
        for (let row = 0; row < rows; row += 1) {
          // Alternating parity is what staggers one row against the next.
          const parity = row % 2;
          const items = [];
          for (let i = 0; i < perRow; i += 1) {
            const isGlyph = cycle.length > 0 && (i + parity) % 2 === 0;
            const key = isGlyph ? cycle[(Math.floor(i / 2) + row) % cycle.length] : null;
            const span1 = key ? (GLYPHS[key] ? GLYPHS[key].width : 1.6) : 0.55 * length;
            items.push({ key, span1 });
          }
          strips.push(items);
        }

        // Rows alternate between leading with a glyph and leading with a name,
        // so they are not the same width. One scale is taken from the widest
        // of them and applied to all, or the rows would end up at different
        // type sizes.
        let widest = 0;
        strips.forEach((items) => {
          let sum = w.gapFactor * (items.length - 1);
          items.forEach((item) => { sum += item.span1; });
          if (sum > widest) widest = sum;
        });

        const natural =
          clamp(w.minSize, w.sizeDivisor / length, w.maxSize) *
          (d.font.sizeFactor || 1) *
          rowScale;

        // The visible arc is what has to hold the strip. Anything past the clip
        // angle is not drawn, so fitting inside it is what guarantees every
        // item survives to the page.
        const visible = span * w.clipFactor;
        const unit =
          widest > 0 && natural * widest > visible
            ? (visible / widest) * 0.99
            : natural;

        const gap = w.gapFactor * unit;
        const rowGap = w.rowGap * rowScale;
        const firstTop = w.centre - ((rows - 1) / 2) * rowGap;

        const shadow = d.ink.light
          ? '0 0.06em 0.1em rgba(0,0,0,.18)'
          : '0 0.05em 0.1em rgba(0,0,0,.22)';

        const fragment = document.createDocumentFragment();

        for (let row = 0; row < rows; row += 1) {
          const top = firstTop + row * rowGap;

          const items = strips[row].map((item) => ({
            key: item.key,
            width: item.span1 * unit,
          }));
          let total = w.gapFactor * unit * (items.length - 1);
          items.forEach((item) => { total += item.width; });

          let cursor = (span - total) / 2;
          items.forEach((item) => {
            const centre = cursor + item.width / 2;
            cursor += item.width + gap;

            const theta = (centre / span - 0.5) * 2 * arc;
            // Belt and braces. The strip is now sized to sit inside the clip
            // angle, so this cannot fire; it stays so a hand-edited geometry
            // setting still degrades to a drawn band rather than a broken one.
            if (Math.abs(theta) > arc * w.clipFactor) return;

            const squash = Math.cos(theta);
            const drop = -(1 - Math.cos(theta)) * w.droop;
            const left = 50 + (halfWidth * Math.sin(theta)) / Math.sin(arc);

            const node = document.createElement('span');
            node.className = 'cpwb-wrap__item';
            node.style.left = `${left.toFixed(3)}%`;
            node.style.top = `${top.toFixed(2)}%`;
            // The lettering angle, as the approved design applies it: the
            // name skews and goes italic, and the icon rotates and skews with
            // it — a little less, so it leans with the lettering rather than
            // toppling. At 0 nothing is added at all.
            let lean = '';
            if (deg) {
              lean = item.key
                ? ` rotate(${(deg * 0.9).toFixed(1)}deg) skewX(${(deg * 0.5).toFixed(1)}deg)`
                : ` skewX(${deg}deg)`;
            }
            // Glyphs are drawn to their own optical centre already; only
            // the lettering needs lifting onto its ink.
            const sink = item.key
              ? 0
              : inkOffset(d.font.family || '', d.font.weight || 700, d.text);
            node.style.transform =
              `translate(-50%, -50%) translateY(${drop.toFixed(2)}cqw) ` +
              (sink ? `translateY(${sink.toFixed(4)}em) ` : '') +
              `scaleX(${squash.toFixed(3)})` + lean;
            node.style.fontStyle = deg && !item.key ? 'italic' : 'normal';
            node.style.opacity = (0.84 + 0.16 * squash).toFixed(3);
            node.style.color = d.ink.hex || '#f7f7f5';
            node.style.fontSize = `${unit.toFixed(2)}cqw`;

            if (item.key) {
              const glyph = GLYPHS[item.key];
              const size = `${(unit * 1.55 * (glyph ? glyph.scale : 1)).toFixed(2)}cqw`;
              const template = this.querySelector(`[data-cpwb-glyph="${item.key}"]`);
              if (template && 'content' in template) {
                const clone = template.content.cloneNode(true);
                const svg = clone.querySelector('svg');
                if (svg) {
                  svg.style.width = size;
                  svg.style.height = size;
                }
                node.appendChild(clone);
              }
            } else {
              node.textContent = d.text;
              node.style.fontFamily = d.font.family || '';
              node.style.fontWeight = String(d.font.weight || 700);
              node.style.textShadow = shadow;
            }

            fragment.appendChild(node);
          });
        }

        // The items go inside a stage rather than straight into the layer.
        // The layer spans the frame's padding box; the bowl photograph only
        // fills its content box, and on a phone the frame pads 28px top and
        // bottom. The stage takes the content box, so a percentage here is a
        // percentage of the bowl and the centre lands on the band at every
        // width.
        const stage = document.createElement('div');
        stage.className = 'cpwb-wrap__stage';
        stage.appendChild(fragment);
        layer.replaceChildren(stage);
      }

      // Swap the bowl photo to the chosen colourway.
      refs.bases.forEach((img, index) => {
        if (index === this.state.colour) {
          img.removeAttribute('hidden');
          img.setAttribute('loading', 'eager');
        } else {
          img.setAttribute('hidden', '');
        }
      });

      const label = interpolate(config.copy.configLabel, {
        colour: d.colour.label,
        size: d.size.label,
        font: d.font.label,
        pattern: d.pattern.label,
        rows: d.density.label,
        ink: d.ink.label,
      });

      if (refs.configLabel) refs.configLabel.textContent = label;
      // The wrap itself is decorative; describe the result once instead.
      if (refs.wrapSr) refs.wrapSr.textContent = label;
    }

    /* ------------------------------------------------- print colour advice */

    /**
     * Advisory only. This never touches the CTA, never disables an ink and
     * never blocks the form — a tonal wrap is a legitimate choice, and every
     * order gets a proof before anything is printed.
     */
    #renderContrast(d) {
      const { refs, config } = this;
      const box = refs.contrast;
      if (!box) return;

      const cfg = config.contrast || {};
      if (!cfg.enabled) {
        box.hidden = true;
        return;
      }

      const band = d.colour.hex;
      const useRatio = cfg.metric !== 'distance';
      const score = (hex) => (useRatio ? contrastRatio(hex, band) : rgbDistance(hex, band));
      const threshold = useRatio ? cfg.ratioThreshold : cfg.distanceThreshold;

      if (score(d.ink.hex) >= threshold) {
        box.hidden = true;
        return;
      }

      // Suggest whichever available ink sits furthest from the band.
      let best = d.ink;
      let bestScore = -Infinity;
      (config.inks || []).forEach((ink) => {
        const value = score(ink.hex);
        if (value > bestScore) {
          bestScore = value;
          best = ink;
        }
      });

      if (refs.contrastText) {
        refs.contrastText.textContent = interpolate(cfg.template, {
          ink: d.ink.label,
          colour: d.colour.label,
          suggestion: best.label,
        });
      }
      box.hidden = false;
    }

    /* ---------------------------------------------------------------- form */

    #renderForm(d) {
      const { refs } = this;

      if (refs.variantId && d.variant) refs.variantId.value = String(d.variant.id);
      if (refs.quantity) refs.quantity.value = String(d.tier.quantity || 1);

      // Keep the ink radio in sync with the colourway default.
      if (this.state.ink == null) {
        const target = refs.inkInputs[d.inkIndex];
        if (target) target.checked = true;
      }

      const setText = (ref, value) => {
        if (ref) ref.textContent = value;
      };

      refs.colourLabels.forEach((node) => setText(node, d.colour.label));
      refs.sizeLabels.forEach((node) => setText(node, d.size.label));
      setText(refs.inkLabel, d.ink.label);
      setText(refs.fontLabel, d.font.label);
      setText(refs.patternLabel, d.pattern.label);
      setText(refs.densityLabel, d.density.label);
      setText(refs.tiltLabel, d.tilt.label);

      refs.swatches.forEach((button, index) =>
        button.setAttribute('aria-current', index === this.state.colour ? 'true' : 'false')
      );

      // Stock meter
      const batch = this.config.stock.batchSize || 60;
      const left = Number(d.colour.stock) || 0;
      const pct = clamp(0, Math.round(((batch - left) / batch) * 100), 100);
      setText(
        refs.stockLeft,
        interpolate(this.config.copy.stockLeft, { count: left, colour: d.colour.label })
      );
      setText(refs.stockPct, interpolate(this.config.copy.stockClaimed, { percent: pct }));
      if (refs.stockFill) refs.stockFill.style.width = `${pct}%`;

      // Availability on each size card
      refs.sizeInputs.forEach((input, index) => {
        const size = this.config.sizes[index];
        if (!size) return;
        const variant = this.#findVariant(d.colour.value, size.value);
        const unavailable = !variant || !variant.available;
        input.disabled = unavailable && this.config.stock.hideUnavailable;
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('cpsb-size--unavailable', unavailable);
      });

      // Availability on each colour chip
      refs.colourInputs.forEach((input, index) => {
        const colour = this.config.colours[index];
        if (!colour) return;
        const anyAvailable = this.config.sizes.some((size) => {
          const variant = this.#findVariant(colour.value, size.value);
          return variant && variant.available;
        });
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('cpsb-chip--unavailable', !anyAvailable);
      });
    }

    #renderPrices(d) {
      const { refs, config } = this;
      const money = (cents) => formatMoney(cents, config.moneyFormat);

      if (refs.priceNow) refs.priceNow.textContent = money(d.net);

      if (refs.priceWas) {
        refs.priceWas.textContent = money(d.gross);
        refs.priceWas.hidden = d.savePct <= 0;
      }
      if (refs.priceSave) {
        refs.priceSave.textContent = interpolate(config.copy.savings, {
          amount: money(d.saved),
          percent: d.savePct,
        });
        refs.priceSave.hidden = d.savePct <= 0;
      }
      if (refs.installment) {
        refs.installment.textContent = interpolate(config.copy.installment, {
          amount: money(Math.round(d.net / 4)),
        });
      }

      // Per-size price labels track the real variant prices for this colour.
      refs.sizePrices.forEach((node, index) => {
        const size = config.sizes[index];
        if (!size) return;
        const variant = this.#findVariant(d.colour.value, size.value);
        node.textContent = variant ? money(variant.price) : '';
      });

      // Bundle tier maths
      refs.tierPrices.forEach((node, index) => {
        const tier = config.tiers[index];
        if (!tier) return;
        const gross = d.unit * tier.quantity;
        const net = Math.max(0, gross - tierOff(d.unit, tier));
        node.textContent = money(net);

        const saveNode = refs.tierSaves[index];
        if (saveNode) {
          saveNode.textContent =
            gross > net ? interpolate(config.copy.tierSave, { amount: money(gross - net) }) : '';
        }
        const tagNode = refs.tierTags[index];
        if (tagNode && tagNode.dataset.cpsbTagTemplate) {
          const pct = gross > net ? Math.round(((gross - net) / gross) * 100) : 0;
          tagNode.textContent = interpolate(tagNode.dataset.cpsbTagTemplate, { percent: pct });
        }
      });
    }

    #renderValidation(d) {
      const { refs, config } = this;
      const used = this.state.name.length;

      if (refs.counter) {
        refs.counter.textContent = interpolate(config.copy.counter, {
          used,
          max: config.validation.maxLength,
        });
      }

      if (refs.hint) {
        refs.hint.textContent = d.valid ? config.copy.hintValid : config.copy.hintEmpty;
      }

      const soldOut = !d.variant || !d.variant.available;
      const blocked = !d.valid || soldOut;
      this.#lastValid = !blocked;

      if (refs.nameInput) {
        refs.nameInput.dataset.cpsbValid = d.valid ? 'true' : 'false';
        refs.nameInput.setAttribute('aria-invalid', d.valid ? 'false' : 'true');
      }

      if (refs.error) {
        refs.error.textContent = soldOut
          ? config.copy.soldOut
          : d.valid
            ? ''
            : config.copy.nameRequired;
      }

      /* Kept in step with cp-signature-bowl.js, which this file is a fork of.
         A disabled button fires no click, so the guard below — which exists to
         take the shopper to the field they have not filled in — could never
         run. Where the section supplies the copy for it, the button stays live
         and carries aria-disabled instead: the same meaning to assistive
         technology, the same unavailable look, but it can be answered.

         Sold out stays truly disabled. There is nothing the shopper can do
         about it, so there is nowhere to send them. */
      const askForName = Boolean(config.copy.ctaEmpty) && !soldOut && !d.valid;

      if (refs.cta) {
        refs.cta.dataset.cpsbSoldOut = soldOut ? 'true' : 'false';
        refs.cta.disabled = blocked && !askForName;
        if (askForName) {
          refs.cta.setAttribute('aria-disabled', 'true');
        } else {
          refs.cta.removeAttribute('aria-disabled');
        }
      }
      if (refs.ctaLabel) {
        refs.ctaLabel.textContent = soldOut
          ? config.copy.ctaSoldOut
          : askForName
            ? config.copy.ctaEmpty
            : interpolate(config.copy.cta, { price: formatMoney(d.net, config.moneyFormat) });
      }
    }

    /**
     * Draw the eye to the field the shopper has to fill in. Shares the
     * cpsb-nudge keyframes in cp-signature-bowl.css with the hero this file
     * forked from, so the two pages move the same way.
     */
    #nudge(node) {
      if (!node) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      node.classList.remove('cpsb-nudge');
      /* Restart the animation even when it is already running: reading back a
         layout property flushes the removal before the class is added again. */
      void node.offsetWidth;
      node.classList.add('cpsb-nudge');
      window.setTimeout(() => node.classList.remove('cpsb-nudge'), 700);
    }

    /** The font swatches preview the customer's actual name, as in the design. */
    #renderFontSamples() {
      const sample = this.state.name.trim() || this.config.copy.fontSampleFallback;
      this.refs.fontSamples.forEach((node, index) => {
        const font = this.config.fonts[index];
        if (!font) return;
        if (node.dataset.cpsbHasSwatch === 'true') return;
        node.textContent = font.caps ? sample.toUpperCase() : sample;
        const size = Math.max(
          11,
          Math.min(21, 74 / Math.max(sample.length, 3)) * (font.sizeFactor || 1)
        );
        node.style.setProperty('--cpsb-sample-size', `${size.toFixed(0)}px`);
      });
    }

    #broadcast(d) {
      document.dispatchEvent(
        new CustomEvent(STATE_EVENT, {
          bubbles: true,
          detail: {
            sectionId: this.dataset.cpwbSection,
            name: d.trimmed,
            colour: d.colour.label,
            size: d.size.label,
            tierTitle: d.tier.title,
            image: (this.refs.bases[this.state.colour] || {}).currentSrc || '',
            priceNow: formatMoney(d.net, this.config.moneyFormat),
            priceWas: formatMoney(d.gross, this.config.moneyFormat),
            hasSavings: d.savePct > 0,
            valid: d.valid,
            formId: this.dataset.cpwbForm || '',
          },
        })
      );
    }
  }

  /* ------------------------------------------------------------- register */

  if (!window.customElements.get('cpwb-personalizer')) {
    window.customElements.define('cpwb-personalizer', CpwbPersonalizer);
  }
})();


/* ---------------------------------------------------------------------------
   Colour row — bring the chosen card into view

   The row starts at scroll position zero, which is right when the chosen
   colourway is the first one and wrong the moment a variant URL selects a
   later one. This nudges the row so the chosen card is the one on screen.

   scrollLeft is set directly rather than calling scrollIntoView, which would
   scroll the page vertically as well and move Step 2 out from under the
   customer. Rects are used instead of offsetLeft so no assumption is made
   about which ancestor is positioned.

   It runs once and stands down the moment the customer touches the row, so it
   can never fight a scroll in progress.
   ------------------------------------------------------------------------ */
(function () {
  var MOBILE = '(max-width: 749px)';

  function align(row) {
    var checked = row.parentNode.querySelector('input:checked');
    if (!checked || !checked.id) return;
    var label;
    try {
      label = row.querySelector(
        'label[for="' + (window.CSS && CSS.escape ? CSS.escape(checked.id) : checked.id) + '"]'
      );
    } catch (error) {
      return;
    }
    if (!label) return;
    var pad = parseFloat(getComputedStyle(row).paddingLeft) || 0;
    var delta = label.getBoundingClientRect().left - row.getBoundingClientRect().left - pad;
    if (Math.abs(delta) < 1) return;
    row.scrollLeft = Math.max(0, row.scrollLeft + delta);
  }

  function arm() {
    if (!window.matchMedia(MOBILE).matches) return;
    var rows = document.querySelectorAll('[data-cpsb-colour-row] .cpsb-chips');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.dataset.cpsbColourArmed === 'on') return;
      row.dataset.cpsbColourArmed = 'on';
      var touched = false;
      ['pointerdown', 'wheel', 'touchstart'].forEach(function (event) {
        row.addEventListener(event, function () { touched = true; }, { passive: true, once: true });
      });
      window.requestAnimationFrame(function () {
        if (!touched && row.scrollWidth > row.clientWidth) align(row);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', arm);
})();


/* ==========================================================================
   Crumpaw · Wraparound Bowl — the Step 2 preview window

   A bolt-on, deliberately separate from <cpwb-personalizer> above: it only
   reads the preview frame that component renders and mirrors it, so nothing
   here can affect what the band draws or what the form submits.

   Two jobs, both mobile-only and both inert above 749px:

   1. The mirror. The personalizer caches node references and writes the band
      through them, so a clone made afterwards is never written to directly.
      The copy is instead kept in step with the real frame, and deliberately
      not by rebuilding it: re-cloning on every keystroke would recreate the
      <img> elements and make them re-decode. Attributes and text are synced in
      place, and the copy is only rebuilt when the structure itself changes.

      The copy is scrubbed of data hooks and ids, because the personalizer runs
      querySelector against those and an unscrubbed duplicate could answer
      first.

   2. The window. The band's position depends on the preview padding and the
      wrap geometry, so it is measured rather than assumed: the copy is shifted
      so the band lands in the middle of the crop, and scaled down only if the
      band would otherwise not fit.

   No dependencies. Nothing here mutates the shared stylesheet or script.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';
  var FIT_MARGIN = 8;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function isHook(name) {
    return (
      name.indexOf('data-cpsb') === 0 ||
      name.indexOf('data-cpwb') === 0 ||
      name === 'id' ||
      name === 'for'
    );
  }

  function scrub(node) {
    if (node.nodeType !== 1) return;
    Array.prototype.slice.call(node.attributes).forEach(function (attr) {
      if (isHook(attr.name)) node.removeAttribute(attr.name);
    });
    node.removeAttribute('fetchpriority');
    Array.prototype.forEach.call(node.children, scrub);
  }

  function copyAttributes(from, to, isRoot) {
    var wanted = {};
    Array.prototype.forEach.call(from.attributes, function (attr) {
      if (isHook(attr.name)) return;
      if (isRoot && attr.name === 'class') return; /* the copy carries its own */
      wanted[attr.name] = attr.value;
    });
    Array.prototype.slice.call(to.attributes).forEach(function (attr) {
      if (attr.name in wanted) return;
      if (attr.name === 'aria-hidden') return;
      if (isRoot && (attr.name === 'class' || attr.name === 'style')) return;
      to.removeAttribute(attr.name);
    });
    Object.keys(wanted).forEach(function (name) {
      if (to.getAttribute(name) !== wanted[name]) to.setAttribute(name, wanted[name]);
    });
  }

  /** Returns false when the shapes have diverged and a rebuild is needed. */
  function sync(from, to, isRoot) {
    if (from.tagName !== to.tagName) return false;
    if (from.children.length !== to.children.length) return false;
    copyAttributes(from, to, isRoot);
    if (!from.children.length) {
      if (to.textContent !== from.textContent) to.textContent = from.textContent;
      return true;
    }
    for (var i = 0; i < from.children.length; i++) {
      if (!sync(from.children[i], to.children[i], false)) return false;
    }
    return true;
  }

  function build(frame, into) {
    var copy = frame.cloneNode(true);
    scrub(copy);
    copy.setAttribute('aria-hidden', 'true');
    into.replaceChildren(copy);
    return copy;
  }

  /** How much is pinned above the section right now. */
  function pinnedHeight(root) {
    var selector = root.getAttribute('data-cpwb-sticky-selector');
    if (!selector) return 0;
    var total = 0;
    var seen = [];
    selector.split(',').forEach(function (part) {
      var sel = part.trim();
      if (!sel) return;
      var nodes;
      try {
        nodes = document.querySelectorAll(sel);
      } catch (error) {
        return; /* a mistyped selector must never break the page */
      }
      Array.prototype.forEach.call(nodes, function (node) {
        if (seen.indexOf(node) !== -1) return;
        var position = window.getComputedStyle(node).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        var rect = node.getBoundingClientRect();
        if (rect.height <= 0) return;
        seen.push(node);
        total += rect.height;
      });
    });
    return total;
  }

  /* Centre the crop on the printed band, and shrink only if the band would
     not otherwise fit. Measured rather than assumed, because the band's
     position follows the wrap centre and row gap settings. */
  function fitBand(crop, copy) {
    var room = crop.clientHeight;
    if (!room) return;

    copy.style.transform = '';

    var items = copy.querySelectorAll('.cpwb-wrap__item');
    if (!items.length) return;

    var base = copy.getBoundingClientRect();
    var top = Infinity;
    var bottom = -Infinity;
    Array.prototype.forEach.call(items, function (el) {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      top = Math.min(top, r.top - base.top);
      bottom = Math.max(bottom, r.bottom - base.top);
    });
    if (top === Infinity || bottom <= top) return;

    var height = bottom - top;
    var centre = (top + bottom) / 2;
    var usable = Math.max(0, room - FIT_MARGIN * 2);
    var scale = height > usable && height ? usable / height : 1;

    copy.style.transform =
      'translateY(' + (room / 2 - centre * scale).toFixed(1) + 'px) scale(' +
      scale.toFixed(4) + ')';
  }

  function Panel(root) {
    this.root = root;
    this.panel = root.querySelector('[data-cpwb="panel"]');
    this.crop = root.querySelector('[data-cpwb="panel-crop"]');
    this.dialog = root.querySelector('[data-cpwb="full"]');
    this.fullBody = root.querySelector('[data-cpwb="full-body"]');
    this.fullConfig = root.querySelector('[data-cpwb="full-config"]');
    this.fullBtn = root.querySelector('.cpwb-fullbtn');
    this.copy = null;
    this.fullCopy = null;
    this.frame = null;
    this.queued = false;
    this.mq = window.matchMedia(MOBILE);
    this.init();
  }

  Panel.prototype.init = function () {
    var self = this;
    if (!this.crop && !this.dialog) return;

    this.reveal();
    this.schedule();

    if (window.MutationObserver) {
      this.observer = new MutationObserver(function () { self.schedule(); });
      this.watch();
    }

    ['change', 'input'].forEach(function (type) {
      self.root.addEventListener(type, function () { self.schedule(); }, true);
    });
    window.addEventListener('load', function () { self.schedule(); }, { once: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.schedule(); }).catch(function () {});
    }

    var onMedia = function () {
      // Crossing 750px changes whether the window belongs on screen at all.
      self.reveal();
      self.schedule();
    };
    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', onMedia);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(onMedia); /* older Safari */
    }

    window.addEventListener('resize', function () { self.schedule(); }, { passive: true });

    this.root.querySelectorAll('[data-cpwb="open-full"]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.openFull(btn); });
    });
    this.root.querySelectorAll('[data-cpwb="close-full"]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.closeFull(); });
    });
    if (this.dialog) {
      // Clicking the backdrop resolves to the dialog element itself.
      this.dialog.addEventListener('click', function (event) {
        if (event.target === self.dialog) self.closeFull();
      });
      this.dialog.addEventListener('close', function () {
        if (self.opener && self.opener.isConnected) self.opener.focus();
      });
    }
  };

  Panel.prototype.reveal = function () {
    var narrow = this.mq.matches;
    if (this.panel) this.panel.hidden = !narrow;
    if (this.fullBtn) this.fullBtn.hidden = !narrow;
  };

  Panel.prototype.watch = function () {
    var frame = this.root.querySelector('[data-cpsb="preview-frame"]');
    if (!frame || !this.observer) return;
    if (this.frame === frame) return;
    this.frame = frame;
    this.observer.disconnect();
    this.observer.observe(frame, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
  };

  Panel.prototype.schedule = function () {
    var self = this;
    if (this.queued) return;
    this.queued = true;
    window.requestAnimationFrame(function () {
      self.queued = false;
      self.paint();
    });
  };

  Panel.prototype.paint = function () {
    if (!this.mq.matches) return;
    var frame = this.root.querySelector('[data-cpsb="preview-frame"]');
    if (!frame) return;
    this.watch();

    if (this.crop) {
      if (!this.copy || !this.copy.isConnected || !sync(frame, this.copy, true)) {
        this.copy = build(frame, this.crop);
      }
      fitBand(this.crop, this.copy);
      if (this.panel) this.panel.dataset.cpwbReady = 'on';
      if (this.root.getAttribute('data-cpwb-sticky') === 'on') {
        this.root.style.setProperty(
          '--cpwb-panel-top',
          Math.round(pinnedHeight(this.root)) + 'px'
        );
      }
    }

    if (this.fullCopy && this.dialog && this.dialog.open) {
      if (!this.fullCopy.isConnected || !sync(frame, this.fullCopy, true)) {
        this.fullCopy = build(frame, this.fullBody);
      }
    }
  };

  Panel.prototype.openFull = function (trigger) {
    if (!this.dialog || !this.fullBody) return;
    var frame = this.root.querySelector('[data-cpsb="preview-frame"]');
    if (frame) this.fullCopy = build(frame, this.fullBody);
    if (this.fullConfig) {
      var label = this.root.querySelector('[data-cpsb="preview-config"]');
      this.fullConfig.textContent = label ? label.textContent : '';
    }
    this.opener = trigger || null;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  };

  Panel.prototype.closeFull = function () {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.cpwb-hero'), function (root) {
      if (root.dataset.cpwbPanelBound === 'on') return;
      root.dataset.cpwbPanelBound = 'on';
      // eslint-disable-next-line no-new
      new Panel(root);
    });
  }

  ready(boot);
  document.addEventListener('shopify:section:load', boot);
})();
